/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTimezone } from '@app/core/hooks/useTimezone';
import { AudioSelect } from '@app/modules/audit/components/audio-select/AudioSelect';
import { createManualMatchValidation } from '@app/modules/audit/screens/matches-add/validation';
import { CreateManualMatchDto } from '@app/modules/audit/types';
import { AuditAudio, DateTimeStr, Milliseconds, now } from '@mi-front/core';
import { DateTimeField, Form, Modal, NumberField, SwitchField } from '@mi-front/ui';
import { useFormikContext } from 'formik';
import moment from 'moment-timezone';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Station } from '@app/core/types/stations';

interface Props {
  station: Station;
  datetime?: DateTimeStr;
  submitFn: (data: CreateManualMatchDto) => Promise<any>;
  cancel: () => any;
}

export type ManualMatchFormValues = {
  audio: AuditAudio | null;
  datetime: string;
  score: string;
  playedDuration: string;
  useAudioDuration: boolean;
};

export function ManualMatchForm({ station, datetime, submitFn, cancel }: Props) {
  const { $t } = useIntl();
  const tz = useTimezone();

  return (
    <Form<ManualMatchFormValues>
      initialValues={{
        audio: null,
        datetime: datetime || now(),
        score: '100',
        playedDuration: '',
        useAudioDuration: true,
      }}
      validationSchema={createManualMatchValidation}
      onSubmit={(data) => {
        return submitFn({
          datetime: data.datetime as DateTimeStr,
          score: data.score ? Number(data.score) : undefined,
          playedDuration: data.playedDuration
            ? (Number(data.playedDuration) as Milliseconds)
            : undefined,
          audioId: data.audio!.id,
        });
      }}
      onCancel={cancel}
      actions={(formikProps, defaultActions) => <Modal.Footer>{defaultActions}</Modal.Footer>}
    >
      <Modal.Body>
        <PlayedDurationUpdater />

        <div className="row">
          <div className="col-12">
            <AudioSelect
              stationId={station.id}
              label={$t({
                id: 'audit.filesUpload.audioIdLabel',
                defaultMessage: 'Media ID:',
              })}
              name="audio"
              multiple={false}
              markRequired
            />
          </div>
        </div>

        <div className="row">
          <div className="col-10">
            <DateTimeField
              name="datetime"
              dateLabel={$t({
                id: 'common.text.date',
                defaultMessage: 'Fecha',
              })}
              timeLabel={$t({
                id: 'common.text.time',
                defaultMessage: 'Hora',
              })}
              isOutsideRange={(date) => moment(date) > moment()}
              tz={tz}
              withSeconds
              markRequired
            />
          </div>
        </div>

        <div className="row">
          <div className="col-4">
            <NumberField
              name="score"
              label={$t({
                id: 'audit.props.score',
                defaultMessage: 'Score',
              })}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-6">
            <NumberField
              name="playedDuration"
              label={$t({
                id: 'audit.matchesAdd.playedDuration',
                defaultMessage: 'Duración reproducida',
              })}
            />
            <SwitchField size="sm" name="useAudioDuration" />
            &nbsp;&nbsp;
            <small>
              {$t({
                id: 'audit.matchesAdd.useAudioDuration',
                defaultMessage: 'Usar duración del audio',
              })}
            </small>
          </div>
        </div>
      </Modal.Body>
    </Form>
  );
}

const PlayedDurationUpdater = () => {
  const { values, setFieldValue } = useFormikContext<ManualMatchFormValues>();

  useEffect(() => {
    if (values.useAudioDuration && values.audio) {
      setFieldValue('playedDuration', values.audio.duration);
    }
  }, [values.audio, values.useAudioDuration]);

  return null;
};
